import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { s, authHeaders, Toast, getODataRows } from './listPageStyles';
import { usePageMeta } from '../hooks/usePageMeta';
import { useC360List } from './c360';
import { unwrapLookupItems } from './c360';
import { Pagination } from './ui';

function displayDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('tr-TR');
}

function formatNumber(value) {
  const num = Number(value);
  if (Number.isNaN(num)) return '-';
  return num.toLocaleString('tr-TR', { maximumFractionDigits: 2 });
}

export function WarehouseStockResetDetailPage() {
  const navigate = useNavigate();
  const { documentId } = useParams();
  const isViewMode = !!documentId;

  usePageMeta(
    isViewMode ? 'Depo Stok Sıfırlama - Detay' : 'Yeni Depo Stok Sıfırlama',
    isViewMode ? 'Sıfırlama belgesini görüntüleyin.' : 'Depo stok sıfırlama işlemi yapın.'
  );

  const [vendors, setVendors] = useState([]);
  const [allWarehouses, setAllWarehouses] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [productNames, setProductNames] = useState({});
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  // Create mode state
  const [selectedVendor, setSelectedVendor] = useState('');
  const [selectedWarehouse, setSelectedWarehouse] = useState('');

  // Cascading: Vendor seçildiğinde sadece o vendor'a ait depoları göster
  const warehouses = selectedVendor
    ? allWarehouses.filter((w) => String(w.vendorId) === String(selectedVendor))
    : [];

  // View mode state
  const [viewData, setViewData] = useState(null);
  const [viewLines, setViewLines] = useState([]);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    fetch('/api/vendors/Select', { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setVendors(unwrapLookupItems(d)))
      .catch(() => {});

    fetch('/api/warehouses/Select', { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((w) => setAllWarehouses(unwrapLookupItems(w)))
      .catch(() => {});
  }, []);

  // Load view mode data
  useEffect(() => {
    if (!isViewMode) return;

    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/stockdocuments/GetById?Id=${documentId}`, { headers: authHeaders() });
        if (!res.ok) throw new Error('Belge alınamadı');
        const body = await res.json();
        const doc = body?.data ?? body;
        setViewData(doc);
        setViewLines(doc?.lines || []);
      } catch (e) {
        showToast('error', e.message || 'Belge yüklenemedi');
        setTimeout(() => navigate('/warehouse-stock-reset'), 2000);
      } finally {
        setLoading(false);
      }
    })();
  }, [isViewMode, documentId, navigate]);

  // Load current stocks for selected warehouse
  useEffect(() => {
    if (!selectedWarehouse) {
      setStocks([]);
      setCurrentPage(1);
      return;
    }

    (async () => {
      setLoading(true);
      try {
        // #798 KAYNAK: bakiye görünümü (vw_StockBalance), ham `Stock` kimlik satırları DEĞİL.
        //
        // Ekran `/api/stocks`ten `QuantityInSmallestUnit` okuyordu; o kolon stok defterinin
        // (StockMovement) türettiği güncel bakiye değil, kimlik satırındaki eski alandır. Sonuç:
        // belge ekranlarının gösterdiği rakamla tutmuyor, hareketi olan bazı ürünler listede hiç
        // görünmüyordu ("ürünler eksik ve depo miktarları hatalı geliyor"). Belge ekranlarıyla
        // AYNI kaynağa bağlandı → iki ekran artık aynı sayıyı söyler.
        const filter = `WarehouseId eq ${selectedWarehouse} and Physical ne 0`;
        const url = `/api/stocks/Balances?$filter=${encodeURIComponent(filter)}&$top=500`;
        const res = await fetch(url, { headers: authHeaders() });
        if (!res.ok) throw new Error('Stoklar alınamadı');
        const data = await res.json();
        // Görünüm `Physical`/`Available` döner; ekranın beklediği alan adına eşlenir. Sıfırlama
        // FİZİKSEL bakiyeye göre yapılır (rezerve düşülmüş `Available` değil) — sayım fiziksel maldır.
        const rows = getODataRows(data).map((r) => ({
          ...r,
          quantityInSmallestUnit: r.physical ?? r.Physical ?? 0,
        }));
        setStocks(rows);
        setCurrentPage(1);

        // Fetch product names for the stocks
        const productIds = [...new Set(rows.map((s) => s.productId).filter(Boolean))];
        if (productIds.length > 0) {
          const filter = productIds.map((id) => `Id eq ${id}`).join(' or ');
          const purl = `/api/products?$top=500&light=1&$filter=${encodeURIComponent(`(${filter})`)}`;
          const pres = await fetch(purl, { headers: authHeaders() });
          if (pres.ok) {
            const pdata = await pres.json();
            const prows = getODataRows(pdata);
            const names = {};
            prows.forEach((p) => {
              names[p.id] = p.name ? `${p.textId} - ${p.name}` : p.textId;
            });
            setProductNames(names);
          }
        }
      } catch (e) {
        showToast('error', e.message || 'Stoklar yüklenemedi');
        setStocks([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedWarehouse]);

  const handleReset = async () => {
    if (!selectedWarehouse) {
      showToast('error', 'Depo seçiniz');
      return;
    }

    setConfirmModal(false);
    setSubmitting(true);

    try {
      const res = await fetch('/api/stockdocuments/ExecuteReset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ warehouseId: selectedWarehouse }),
      });

      const body = await res.json();

      if (!res.ok) {
        const msg = body?.message || body?.Message || 'Sıfırlama başarısız oldu';
        showToast('error', msg);
        return;
      }

      showToast('success', 'Depo stokları başarıyla sıfırlandı');
      setTimeout(() => navigate('/warehouse-stock-reset'), 1500);
    } catch (e) {
      showToast('error', e.message || 'Sıfırlama sırasında hata oluştu');
    } finally {
      setSubmitting(false);
    }
  };

  const vendorLabel = (id) => {
    if (!id) return '-';
    const v = vendors.find((x) => x.id === id);
    return v ? `${v.textId} - ${v.name}` : '-';
  };

  const warehouseLabel = (id) => {
    if (!id) return '-';
    const w = allWarehouses.find((x) => x.id === id);
    return w ? `${w.textId} - ${w.name}` : '-';
  };

  if (isViewMode && loading) {
    return (
      <div style={s.container}>
        <div style={{ padding: 24, textAlign: 'center' }}>Yükleniyor...</div>
      </div>
    );
  }

  if (isViewMode && viewData) {
    return (
      <div style={s.container}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Depo Stok Sıfırlama Detay</h2>
          <button style={s.btnSecondary} onClick={() => navigate('/warehouse-stock-reset')}>Geri Dön</button>
        </div>

        <div style={{ background: 'var(--bg-card, #ffffff)', borderRadius: 8, padding: 20, border: '1px solid var(--border-color, #e5e7eb)', marginBottom: 20 }}>
          <div style={s.formRow}>
            <div style={s.formGroup}>
              <label style={s.label}>Distribütör</label>
              <input style={s.inputView} readOnly value={vendorLabel(viewData.vendorId)} />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Depo</label>
              <input style={s.inputView} readOnly value={warehouseLabel(viewData.warehouseId)} />
            </div>
          </div>

          <div style={s.formRow}>
            <div style={s.formGroup}>
              <label style={s.label}>Tarih</label>
              <input style={s.inputView} readOnly value={displayDate(viewData.documentDate)} />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Açıklama</label>
              <input style={s.inputView} readOnly value={viewData.description || '-'} />
            </div>
          </div>
        </div>

        {viewLines.length > 0 && (
          <div style={{ background: 'var(--bg-card, #ffffff)', borderRadius: 8, padding: 20, border: '1px solid var(--border-color, #e5e7eb)' }}>
            <h3 style={{ margin: '0 0 16 0', fontSize: 16, fontWeight: 700 }}>Sıfırlanan Ürünler ({viewLines.length} ürün)</h3>
            <table style={{
              width: '100%', borderCollapse: 'collapse', marginTop: 8, fontSize: 13,
              borderTop: '1px solid var(--border-color, #e5e7eb)',
            }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-color, #e5e7eb)', background: 'var(--bg-secondary, #f9fafb)' }}>
                  <th style={{ padding: 8, textAlign: 'left' }}>Ürün ID</th>
                  <th style={{ padding: 8, textAlign: 'left' }}>Birim</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>Miktar</th>
                </tr>
              </thead>
              <tbody>
                {viewLines.map((line, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid var(--border-color, #e5e7eb)' }}>
                    <td style={{ padding: 8 }}>{line.productId}</td>
                    <td style={{ padding: 8 }}>{line.unitName || '-'}</td>
                    <td style={{ padding: 8, textAlign: 'right' }}>{formatNumber(line.quantity)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Toast toast={toast} />
      </div>
    );
  }

  // Create mode
  return (
    <div style={s.container}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <button style={s.btnSecondary} onClick={() => navigate('/warehouse-stock-reset')}>İptal</button>
      </div>

      {/* Selection Section */}
      <div style={{ background: 'var(--bg-card, #ffffff)', borderRadius: 8, padding: 20, border: '1px solid var(--border-color, #e5e7eb)', marginBottom: 20 }}>
        <h3 style={{ margin: '0 0 16 0', fontSize: 14, fontWeight: 700 }}>Depo Seçimi</h3>
        <div style={s.formRow}>
          <div style={s.formGroup}>
            <label style={s.label}>Distribütör</label>
            <select
              style={s.select}
              value={selectedVendor}
              onChange={(e) => {
                setSelectedVendor(e.target.value);
                setSelectedWarehouse('');
              }}
            >
              <option value="">Distribütör Seçiniz</option>
              {vendors.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.textId} - {v.name}
                </option>
              ))}
            </select>
          </div>
          <div style={s.formGroup}>
            <label style={s.label}>Depo</label>
            <select
              style={{...s.select, opacity: selectedVendor ? 1 : 0.5, cursor: selectedVendor ? 'pointer' : 'not-allowed'}}
              value={selectedWarehouse}
              onChange={(e) => setSelectedWarehouse(e.target.value)}
              disabled={!selectedVendor}
            >
              <option value="">Depo Seçiniz</option>
              {warehouses.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.textId} - {w.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Current Stocks Section */}
      {selectedWarehouse && (
        <div style={{ background: 'var(--bg-card, #ffffff)', borderRadius: 8, padding: 20, border: '1px solid var(--border-color, #e5e7eb)', marginBottom: 20 }}>
          <h3 style={{ margin: '0 0 16 0', fontSize: 14, fontWeight: 700 }}>
            Güncel Depodaki Stoklar ({stocks.length})
          </h3>

          {loading ? (
            <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-secondary, #4b5563)' }}>
              Yükleniyor...
            </div>
          ) : stocks.length === 0 ? (
            <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-secondary, #4b5563)' }}>
              Bu depoda stok bulunmamaktadır.
            </div>
          ) : (
            <>
              <table style={{
                width: '100%', borderCollapse: 'collapse', fontSize: 13,
                borderTop: '1px solid var(--border-color, #e5e7eb)',
              }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border-color, #e5e7eb)', background: 'var(--bg-secondary, #f9fafb)' }}>
                    <th style={{ padding: 8, textAlign: 'left' }}>Ürün</th>
                    <th style={{ padding: 8, textAlign: 'right' }}>Miktar</th>
                  </tr>
                </thead>
                <tbody>
                  {stocks.slice((currentPage - 1) * pageSize, currentPage * pageSize).map((stock) => (
                    <tr key={stock.id} style={{ borderBottom: '1px solid var(--border-color, #e5e7eb)' }}>
                      <td style={{ padding: 8 }}>{productNames[stock.productId] || stock.productId}</td>
                      <td style={{ padding: 8, textAlign: 'right' }}>{formatNumber(stock.quantityInSmallestUnit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {stocks.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <Pagination
                    currentPage={currentPage}
                    totalCount={stocks.length}
                    pageSize={pageSize}
                    rowCount={Math.min(pageSize, stocks.length - (currentPage - 1) * pageSize)}
                    onPageChange={setCurrentPage}
                    onPageSizeChange={setCurrentPage}
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Action Buttons */}
      {selectedWarehouse && stocks.length > 0 && (
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 20 }}>
          <button
            style={s.btnSecondary}
            onClick={() => navigate('/warehouse-stock-reset')}
            disabled={submitting}
          >
            İptal
          </button>
          <button
            style={{ ...s.btnPrimary, opacity: submitting ? 0.6 : 1, cursor: submitting ? 'not-allowed' : 'pointer' }}
            onClick={() => setConfirmModal(true)}
            disabled={submitting}
          >
            {submitting ? 'İşleniyor...' : 'Sıfırla'}
          </button>
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmModal && (
        <div style={s.overlay} onClick={() => setConfirmModal(false)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>Depo Stok Sıfırlama Onayı</h3>
            </div>
            <div style={s.modalBody}>
              <p style={{ margin: '0 0 12 0' }}>
                <strong>{warehouseLabel(selectedWarehouse)}</strong> deposindeki seçili tüm stoklar sıfırlanacaktır.
              </p>
              <p style={{ margin: '8 0 0 0', fontSize: 13, color: 'var(--text-secondary, #4b5563)' }}>
                • Toplam <strong>{stocks.length} ürün</strong> sıfırlanacak
              </p>
              <p style={{ margin: '8 0 16 0', fontSize: 13, color: 'var(--text-secondary, #4b5563)' }}>
                • Bu işlem geri alınamaz ve stok çıkış hareketi oluşturacaktır
              </p>
              <p style={{ margin: 0, fontSize: 14, color: 'var(--text-primary, #1a202c)', fontWeight: 600 }}>
                Onaylıyor musunuz?
              </p>
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnSecondary} onClick={() => setConfirmModal(false)} disabled={submitting}>
                İptal
              </button>
              <button style={s.btnDanger} onClick={handleReset} disabled={submitting}>
                {submitting ? 'İşleniyor...' : 'Evet, Sıfırla'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}
